import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import {
  DEFAULT_COMPANY_LABEL_ZPL_TEMPLATES,
  LABEL_ZPL_TEMPLATES_SETTING_KEY,
  parseCompanyLabelZplTemplates,
  type CompanyLabelZplTemplates,
} from "./labelZplTemplates";

export const LABEL_ZPL_TEMPLATES_QUERY_KEY = [
  "company_settings",
  LABEL_ZPL_TEMPLATES_SETTING_KEY,
] as const;

interface UseLabelZplTemplatesResult {
  templates: CompanyLabelZplTemplates;
  loading: boolean;
  companyId: string | null;
}

/**
 * Read company ZPL template overrides from `company_settings`.
 * Empty object means all kinds use shipped defaults.
 */
export function useLabelZplTemplates(): UseLabelZplTemplatesResult {
  const { id: companyId } = useCurrentCompany();

  const { data, isLoading } = useQuery({
    queryKey: [...LABEL_ZPL_TEMPLATES_QUERY_KEY, companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<CompanyLabelZplTemplates> => {
      const { data } = await (supabase as any)
        .from("company_settings")
        .select("setting_value")
        .eq("company_id", companyId)
        .eq("setting_key", LABEL_ZPL_TEMPLATES_SETTING_KEY)
        .maybeSingle();
      return parseCompanyLabelZplTemplates(data?.setting_value);
    },
  });

  return {
    templates: data ?? { ...DEFAULT_COMPANY_LABEL_ZPL_TEMPLATES },
    loading: isLoading,
    companyId,
  };
}

export function useInvalidateLabelZplTemplates() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: LABEL_ZPL_TEMPLATES_QUERY_KEY });
}
